import {
  FC,
  FormEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import {
  Alert,
  Button,
  TextField,
  Stack,
  Typography,
  CircularProgress,
} from '@mui/material';
import LoginFormPasswordField from './LoginFormPasswordField';
import { Link, useYupValidationResolver } from '@vs-online-only-app/ui';
import {
  AuthenticationContext,
  LoginFormFieldValues,
} from '@vs-online-only-app/state';
import EmailLink from '../Views/reset-password/EmailLink';

const validationSchema = yup.object({
  email: yup
    .string()
    .required('Required')
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Invalid'), // https://github.com/jquense/yup/issues/507
  password: yup.string().required('Required'),
});

export function isValueAnEmail(value: string) {
  return value.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
}

const LoginForm: FC = () => {
  const resolver = useYupValidationResolver(validationSchema);
  const { login, loginStatus } = useContext(AuthenticationContext);
  const isAuthLoading = loginStatus === 'loading';
  const [errorMessage, setErrorMessage] = useState('');
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (loginStatus === 'error') {
      setErrorMessage(
        'Incorrect email address or password. Please check and try again.',
      );
    }
  }, [setErrorMessage, loginStatus]);

  const formMethods = useForm<LoginFormFieldValues>({
    defaultValues: {
      email: decodeURIComponent(searchParams.get('email') || ''),
      password: '',
    },
    resolver,
  });

  const { errors } = formMethods.formState;

  const handleFieldBlur = async (fieldName: keyof LoginFormFieldValues) => {
    formMethods.trigger(fieldName);
  };

  const handleFieldChange = useCallback(
    (params: { field: keyof LoginFormFieldValues; value: string }) => {
      if (
        params.field === 'email' &&
        errorMessage !== '' &&
        isValueAnEmail(params.value)
      ) {
        setErrorMessage('');
      }
      formMethods.setValue(params.field, params.value);
    },
    [errorMessage, formMethods],
  );

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault();
    formMethods.handleSubmit(async (formValues) => {
      const { email, password } = formValues;
      login({ email, password });
    })();
  };

  return (
    <Stack gap={2}>
      <Typography align="center" variant="h4">
        Sign in
      </Typography>
      <FormProvider {...formMethods}>
        <form onSubmit={handleFormSubmit}>
          <Stack gap={2}>
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
            <TextField
              {...formMethods.register('email')}
              label={`Email address${
                errors?.email?.message ? ` - ${errors.email.message}` : ''
              }`}
              variant="outlined"
              fullWidth
              error={!!errors.email}
              onBlur={() => handleFieldBlur('email')}
              onChange={(e) =>
                handleFieldChange({ field: 'email', value: e.target.value })
              }
            />
            <LoginFormPasswordField
              {...formMethods.register('password')}
              error={!!errors.password}
              onBlur={() => handleFieldBlur('password')}
              onChange={(value) =>
                handleFieldChange({ field: 'password', value })
              }
            />
            <EmailLink
              email={formMethods.getValues().email}
              to="/reset-password"
            >
              Reset password
            </EmailLink>
            <Button
              variant="contained"
              size="large"
              type="submit"
              fullWidth
              disabled={isAuthLoading}
              sx={{
                mt: 2,
              }}
            >
              {isAuthLoading ? <CircularProgress size={20} /> : 'Login'}
            </Button>
            <Typography align="center" variant="body2" color="textSecondary">
              By using the Store App, you agree to our{' '}
              <Link to="https://bambuser.com/privacy-policy" target="_blank">
                Privacy Policy
              </Link>
              .
            </Typography>
          </Stack>
        </form>
      </FormProvider>
    </Stack>
  );
};

export default LoginForm;
